import React, { useState, useEffect } from 'react';
import styled from '@emotion/styled';
import { useTableUniverse, useTableApi } from '~Components/TableUniverse';
import { useButton, useI18n } from '~/Hooks';
import PenIcon from '~/Assets/icons/edit.svg';
import Modal from '~ComponentsImproved/Modal';
import { ModalContent } from '~Styles/modal';
import MessageInput from '~ComponentsImproved/MessageInput';

const Wrapper = styled.div`
  position: relative;
  justify-content: center;
  align-items: center;
`;

const ButtonWrapper = styled.div<{ touching: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  transform: scale(${(props) => (props.touching ? 0.95 : 1)});
`;

const Icon = styled.span<{
  icon: string;
}>`
  height: 24px;
  width: 24px;
  mask-image: url(${(props) => props.icon});
  mask-position: center;
  mask-size: contain;
  mask-repeat: no-repeat;
  background-color: #545960;
`;

const CreateNoteText = styled.div`
  color: #c3c5c7;
  font-weight: bold;
  font-size: 14px;
  font-weight: 600;
  letter-spacing: 0.24px;
`;

const TextArea = styled.textarea`
  height: 240px;
  width: 100%;
  margin-bottom: 16px;
  padding: 8px;
  box-sizing: border-box;
  font-family: 'Eina01', 'HelveticaNeue', Helvetica, Arial, sans-serif;
  border: none;
  overflow: auto;
  outline: none;
  box-shadow: none;
  resize: none;
  font-size: 18px;
  z-index: 999;
  border-radius: 4px;
  line-height: 1.7;
  color: #fff;
  background-color: #1d232d;
`;

const ButtonsWrapper = styled.div`
  width: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
`;

const Button = styled.button<{
  bgColor: string;
}>`
  background: ${(props) => (props?.bgColor ? props.bgColor : '#eeeeee')};
  color: #fff;
  border: none;
  border-radius: 4px;
  padding: 8px 12px;
  font-size: 16px;
  flex: 1;

  align-items: center;
  min-height: 48px;
  border-radius: 4px;
  padding: 12px;
  position: relative;
  font-size: 14px;
  font-weight: 600;
  letter-spacing: 0.24px;
  line-height: 24px;
  text-transform: uppercase;

  &:not(:last-child) {
    margin-right: 8px;
  }
`;

interface Props {
  tableId: string;
  isEditing: boolean;
  setIsEditing: (isEditing: boolean) => void;
}

type TextAreaEvent = React.ChangeEvent<HTMLTextAreaElement>;

const TableNote: React.FunctionComponent<Props> = ({ tableId, isEditing, setIsEditing }) => {
  const { i18n } = useI18n();
  const api = useTableApi();
  const universe = useTableUniverse();
  const note = universe?.notes ? universe?.notes[tableId] : null;

  const [text, setText] = useState('');

  useEffect(() => {
    setText(note?.text || '');
  }, [note]);

  const handleSave = (newText: string) => {
    if (newText === '') {
      api.removeTableNote(tableId);
    } else {
      api.addTableNote(tableId, newText);
    }

    setIsEditing(false);
  };

  const button = useButton({
    onClick: () => setIsEditing(true),
  });

  return (
    <Wrapper>
      <ButtonWrapper {...button}>
        <Icon icon={PenIcon} />
        <CreateNoteText>{i18n('TM.CreateNote')}</CreateNoteText>
      </ButtonWrapper>

      <Modal isShowing={isEditing} hide={() => setIsEditing(false)}>
        <MessageInput
          acceptEmpty
          value={text}
          onSubmit={(msg) => {
            handleSave(msg);
          }}
          autoFocus
        />
      </Modal>
    </Wrapper>
  );
};

export default TableNote;
